// Chart utilities — auto-bucket time series + pad pre-first-input window with zeros.

/**
 * Auto-bucket a time series by span:
 *   - span <= 90 days  -> daily (no aggregation)
 *   - span <= 365 days -> weekly (Mon start)
 *   - else             -> monthly (YYYY-MM)
 * Series points: [{ date: 'YYYY-MM-DD', value: number }]
 * Returns: { series: [{ label, date, value }], granularity }
 */
export function autoBucketSeries(series) {
  if (!Array.isArray(series) || series.length === 0) return { series: [], granularity: 'none' };
  const sorted = [...series].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const first = new Date(sorted[0].date);
  const last = new Date(sorted[sorted.length - 1].date);
  const spanDays = Math.max(1, Math.round((last - first) / 86400000));

  if (spanDays <= 90) {
    return { series: sorted.map((p) => ({ ...p, label: p.date })), granularity: 'day' };
  }

  if (spanDays <= 365) {
    // Bucket by ISO week (Monday)
    const weekMap = new Map();
    for (const p of sorted) {
      const d = new Date(p.date);
      const day = d.getDay();
      const diff = (day === 0 ? -6 : 1 - day);
      const monday = new Date(d);
      monday.setDate(d.getDate() + diff);
      const wk = monday.toISOString().slice(0, 10);
      weekMap.set(wk, p.value); // latest snapshot in the week wins
    }
    return {
      series: [...weekMap.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([wk, v]) => ({ date: wk, label: wk.slice(5), value: v })),
      granularity: 'week',
    };
  }

  // Monthly
  const monthMap = new Map();
  for (const p of sorted) {
    const m = (p.date || '').slice(0, 7);
    if (!m) continue;
    monthMap.set(m, p.value); // latest in month
  }
  return {
    series: [...monthMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([m, v]) => ({ date: `${m}-01`, label: m, value: v })),
    granularity: 'month',
  };
}

/**
 * Pad with zeros from `start` up to (but not including) first data point.
 * Both start and series[0].date must be YYYY-MM-DD strings.
 * Granularity determines step size.
 */
export function padPreFirstZero(series, start, granularity = 'month') {
  if (!series || series.length === 0 || !start) return series || [];
  const firstDate = series[0].date;
  if (firstDate <= start) return series;

  const step = granularity === 'day' ? 1 : granularity === 'week' ? 7 : 30;
  const out = [];
  const startDt = new Date(start);
  const firstDt = new Date(firstDate);
  for (let d = new Date(startDt); d < firstDt; d.setDate(d.getDate() + step)) {
    const iso = d.toISOString().slice(0, 10);
    out.push({ date: iso, label: granularity === 'month' ? iso.slice(0, 7) : iso, value: 0 });
  }
  return [...out, ...series];
}

/**
 * Compute YAxis domain for a series that may include negatives.
 * Adds ~10% headroom on both sides and ensures a clean baseline at 0 when possible.
 */
export function symmetricalDomain(values) {
  if (!values || values.length === 0) return [0, 100];
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const pad = Math.max(Math.abs(min), Math.abs(max)) * 0.1 || 1;
  return [Math.floor(min - pad), Math.ceil(max + pad)];
}
